import { Button, useToast } from "@aidkitorg/component-library";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { usePost, useToken } from "../../API";
import { useLinkKey } from "../../Apply";
import { useLocalizedStrings } from "../../Localization";
import { PublicConfigurationContext } from "../../Context";
import { LIVENESS_REQUEST_EVENT_TYPE } from "../../Questions/LivenessDetection";
import { canReturnToApplication, returnToApplication, saveKeyToCookieAndRelocateToUrl } from "../../Util";
import * as Session from "./Session";
import { LivenessDetection } from "./SessionUIController/types";
import { handleErrors } from "./LivenessErrors";
import { getBrowserDetails } from "./BrowserDetails";
import { usePing } from "../../utils/useMultipartUpload";
import { LivenessDetectionSessionUIContextProvider } from "./SessionUIController";
import { LivenessPreludeExperimental } from "./LivenessPreludeExperimental";

export function LivenessDetectionPage(props: any) {
  useLinkKey();

  const [ready, setReady] = useState(false);
  const L = useLocalizedStrings();
  const { toast } = useToast();
  const publicConfig = useContext(PublicConfigurationContext);

  const { livenessId } = useParams<{ livenessId: string }>();
  const getLivenessSession = usePost("/applicant/get_liveness_session", {
    handleErrors: (data) => {
      handleErrors(data, (payload) => toast(payload));
    },
  });
  const [errored, setErrored] = useState(null as null | string);
  const [, auth_token] = useToken();
  const [livenessConfig, setLivenessConfig] = useState(null as null | LivenessDetection);

  const ping = usePing();

  function sendLivenessRefreshRequest() {
    if (window.opener) {
      // auth needs to be refreshed
      // notify the parent window that we need a new liveness link
      try {
        window.opener.postMessage(
          JSON.stringify({
            type: LIVENESS_REQUEST_EVENT_TYPE,
          }),
        );
      } catch (error) {
        console.warn("Liveness window communication failed:", error);
      }
    }
  }

  useEffect(() => {
    if (!livenessId) return;
    if (!auth_token) {
      sendLivenessRefreshRequest();
      return;
    }

    // Fetch the liveness config
    (async () => {
      const resp = await getLivenessSession({ livenessId });
      // get some details about their browser
      const browserDetails = getBrowserDetails();

      if (resp.error) {
        const pingPromise = ping("Received error from getLivenessSession", { livenessId, ...resp, browserDetails });
        setErrored(resp.error);
        try {
          switch (resp.error) {
            case "ld2_session_empty_please_start_over":
            case "ld2_link_expired":
            case "ld2_session_expired":
            case "ld2_not_authorized":
              return sendLivenessRefreshRequest();
            default:
            // do nothing with other errors
          }
        } finally {
          await pingPromise;
        }
      } else if (resp.livenessConfig) {
        setLivenessConfig(resp.livenessConfig);
      } else {
        await ping("Unable to get liveness config", { livenessId, ...(resp || {}), browserDetails });
        setErrored("ld2_unknown_error");
      }
    })();
  }, [livenessId, auth_token]);

  if (window.location.search.includes("key")) {
    saveKeyToCookieAndRelocateToUrl(window.location.href, { removeHistory: true, useHistoryToRelocate: true });
    return <></>;
  }

  const errorContent = (function getErrorContent() {
    const sessionEmptyContent = (
      <div>
        {L.selfie.session_expired_message}:
        <ol className="list-decimal mb-0 mt-2 list-inside">
          <li>{L.selfie.session_expired_close}</li>
          <li>{L.selfie.session_expired_restart}</li>
        </ol>
      </div>
    );

    if (!auth_token && !window.location.search.includes("key")) {
      return sessionEmptyContent;
    }

    switch (errored) {
      case "ld2_session_empty_please_start_over":
        return sessionEmptyContent;
      case "ld2_liveness_not_defined":
        return <div>There was a Problem getting the configuration</div>;
      case "ld2_link_expired":
      case "ld2_session_expired":
      case "ld2_not_authorized":
        return <div>This session has expired. Please go back to your application and start a new session.</div>;
      default:
        return null;
    }
  })();

  if (errorContent) {
    return (
      <div className="p-2 m-2 flex gap-2 bg-red-200 ring-2 ring-red-400 rounded-md items-start">
        <ExclamationCircleIcon className="w-8 shrink-0" />
        <div className="grow">
          {errorContent}
          {publicConfig.experimental?.livenessSameTab && canReturnToApplication() && (
            <Button variant="secondary" size="sm" className="mt-2" onClick={() => returnToApplication()}>
              {L.liveness.return_to_application}
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!ready) {
    const preludeProps = {
      livenessConfig,
      L,
      setReady,
    };
    return <LivenessPreludeExperimental {...preludeProps} />;
  }

  return (
    <LivenessDetectionSessionUIContextProvider>
      <Session.LivenessDetectionSessionPage {...props} />
    </LivenessDetectionSessionUIContextProvider>
  );
}
